import { Disposable } from '../../utils/Disposable.js';
import { createAbortableTransaction, isAbortError, storeRequestPromise, } from './util.js';
export class IdbService extends Disposable {
    constructor(db, { log } = {}) {
        super();
        this.db = db;
        this.createTransaction = (storeNames, opts) => {
            var _a;
            try {
                if (this.globalAbortController.signal.aborted) {
                    throw new Error('Global abort signal is already aborted');
                }
                const tx = createAbortableTransaction(this.db, storeNames, (opts === null || opts === void 0 ? void 0 : opts.mode) || 'readonly', opts === null || opts === void 0 ? void 0 : opts.abort, this.log);
                this.globalAbortController.signal.addEventListener('abort', tx.abort);
                tx.addEventListener('complete', () => {
                    this.globalAbortController.signal.removeEventListener('abort', tx.abort);
                });
                tx.addEventListener('error', () => {
                    this.globalAbortController.signal.removeEventListener('abort', tx.abort);
                });
                return tx;
            }
            catch (err) {
                (_a = this.log) === null || _a === void 0 ? void 0 : _a.call(this, 'error', 'Failed to create abortable transaction for store names', storeNames, err);
                throw err;
            }
        };
        this.run = async (storeName, getRequest, opts) => {
            var _a;
            if (this.disposed || ((_a = opts === null || opts === void 0 ? void 0 : opts.transaction) === null || _a === void 0 ? void 0 : _a.error))
                return Promise.resolve(undefined);
            const tx = (opts === null || opts === void 0 ? void 0 : opts.transaction) || this.createTransaction([storeName], opts);
            const store = tx.objectStore(storeName);
            const request = getRequest(store);
            return storeRequestPromise(request);
        };
        this.runAll = async (storeName, getRequests, opts) => {
            var _a;
            if (this.disposed || ((_a = opts === null || opts === void 0 ? void 0 : opts.transaction) === null || _a === void 0 ? void 0 : _a.error))
                return Promise.resolve([]);
            const tx = (opts === null || opts === void 0 ? void 0 : opts.transaction) || this.createTransaction([storeName], opts);
            const store = tx.objectStore(storeName);
            const requests = getRequests(store);
            return Promise.all(requests.map(storeRequestPromise));
        };
        this.iterate = async (storeName, getRequest, iterator, opts) => {
            const tx = (opts === null || opts === void 0 ? void 0 : opts.transaction) || this.createTransaction([storeName], opts);
            const store = tx.objectStore(storeName);
            const request = getRequest(store);
            if (Array.isArray(request)) {
                return Promise.all(request.map((req) => {
                    return new Promise((resolve, reject) => {
                        req.onsuccess = () => {
                            const cursor = req.result;
                            if (cursor) {
                                const stop = iterator(cursor.value, store, cursor);
                                if (stop) {
                                    resolve();
                                }
                                else {
                                    cursor.continue();
                                }
                            }
                            else {
                                resolve();
                            }
                        };
                        req.onerror = () => {
                            if (req.error && isAbortError(req.error)) {
                                resolve();
                            }
                            else {
                                reject(req.error);
                            }
                        };
                    });
                })).then(() => undefined);
            }
            return new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        const stop = iterator(cursor.value, store, cursor);
                        if (stop) {
                            resolve();
                        }
                        else {
                            cursor.continue();
                        }
                    }
                    else {
                        resolve();
                    }
                };
                request.onerror = () => {
                    if (request.error && isAbortError(request.error)) {
                        resolve();
                    }
                    else {
                        reject(request.error);
                    }
                };
            });
        };
        this.clear = (storeName, transaction) => {
            return this.run(storeName, (store) => store.clear(), {
                mode: 'readwrite',
                transaction,
            });
        };
        this.onVersionChange = (ev) => {
            var _a, _b;
            (_a = this.log) === null || _a === void 0 ? void 0 : _a.call(this, 'warn', `Another tab has requested a version change for ${this.db.name}`);
            this.db.close();
            if (typeof window !== 'undefined') {
                try {
                    window.location.reload();
                }
                catch (err) {
                    (_b = this.log) === null || _b === void 0 ? void 0 : _b.call(this, 'error', 'Failed to reload the page', err);
                }
            }
        };
        this.log = log;
        const abortController = new AbortController();
        const abort = abortController.abort.bind(abortController);
        this.globalAbortController = abortController;
        // FIXME: replace with event? I can't get this to work.
        // this.addDispose(abort);
        this.db.addEventListener('versionchange', this.onVersionChange);
        this.addDispose(() => {
            this.db.removeEventListener('versionchange', this.onVersionChange);
        });
    }
}
//# sourceMappingURL=IdbService.js.map